import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { CalendarClock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Accepts +country + 7-15 digits, or 7-15 plain digits (loose E.164-ish)
const PHONE_RE = /^\+?[0-9][0-9\s().-]{6,19}$/;

function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function nowTimeHHMM() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ScheduleCallDialog({ lead, users = [], onScheduled }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const defaultPhone = useMemo(() =>
    lead?.phone || lead?.contact_phone || lead?.contact2_phone || "",
  [lead]);

  const defaultContact = useMemo(() =>
    lead?.contact_name || lead?.contact2_name || "",
  [lead]);

  const defaultTitle = useMemo(
    () => `Call with ${lead?.business_name || "lead"}`,
    [lead],
  );

  const [form, setForm] = useState({
    title: defaultTitle,
    date: todayISO(),
    time: nowTimeHHMM(),
    duration: 30,
    assigned_to: currentUser?.user_id || "",
    contact_name: defaultContact,
    phone_number: defaultPhone,
    description: "",
  });

  // Refresh defaults whenever the dialog opens (so re-opens pick up latest lead data)
  useEffect(() => {
    if (open) {
      setForm({
        title: defaultTitle,
        date: todayISO(),
        time: nowTimeHHMM(),
        duration: 30,
        assigned_to: currentUser?.user_id || "",
        contact_name: defaultContact,
        phone_number: defaultPhone,
        description: "",
      });
      if (!currentUser) {
        fetch(`${API}/auth/me`, { credentials: "include" })
          .then((r) => (r.ok ? r.json() : null))
          .then((u) => {
            if (u?.user_id) {
              setCurrentUser(u);
              setForm((f) => ({ ...f, assigned_to: f.assigned_to || u.user_id }));
            }
          })
          .catch(() => {});
      }
    }
  }, [open, defaultTitle, defaultContact, defaultPhone, currentUser]);

  const validate = () => {
    if (!form.title.trim()) return "Call title is required.";
    if (!form.date) return "Date is required.";
    if (!form.time) return "Time is required.";
    if (!form.assigned_to) return "Please assign the call to a team member.";
    if (!form.phone_number || !PHONE_RE.test(form.phone_number.trim())) {
      return "Enter a valid phone number (7–15 digits, optional + prefix).";
    }
    const scheduled = new Date(`${form.date}T${form.time}`);
    if (Number.isNaN(scheduled.getTime())) return "Invalid date/time.";
    if (scheduled.getTime() <= Date.now()) {
      return "Scheduled time must be in the future.";
    }
    const dur = parseInt(form.duration, 10);
    if (!dur || dur < 5 || dur > 240) return "Duration must be 5–240 minutes.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      // Build local ISO with tz offset so the server sees the exact wall time the rep chose
      const scheduled = new Date(`${form.date}T${form.time}`);
      const startISO = scheduled.toISOString();
      const assignedUser = users.find((u) => u.user_id === form.assigned_to);
      const res = await fetch(`${API}/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: form.title.trim(),
          start_time: startISO,
          duration: parseInt(form.duration, 10),
          meeting_type: "phone_call",
          phone_number: form.phone_number.trim(),
          lead_id: lead.lead_id,
          lead_name: lead.business_name,
          contact_name: form.contact_name,
          contact_email: lead.contact_email || lead.email || "",
          assigned_to: form.assigned_to,
          assigned_to_name: assignedUser?.name || "",
          description: form.description,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to schedule call");
      toast.success(`Call scheduled — ${assignedUser?.name || "rep"} notified by email`);
      setOpen(false);
      onScheduled?.(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="schedule-call-btn">
          <CalendarClock className="w-4 h-4 mr-2" />
          Schedule Call
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="schedule-call-dialog">
        <DialogHeader>
          <DialogTitle>Schedule a call</DialogTitle>
          <DialogDescription>
            Saves to the CRM calendar, attaches to this lead, and emails the assigned rep.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="sc-title">Call title</Label>
            <Input
              id="sc-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="sc-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="sc-date">Date</Label>
              <Input
                id="sc-date"
                type="date"
                min={todayISO()}
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="sc-date"
              />
            </div>
            <div>
              <Label htmlFor="sc-time">Time</Label>
              <Input
                id="sc-time"
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                data-testid="sc-time"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="sc-duration">Duration (min)</Label>
              <Input
                id="sc-duration"
                type="number"
                min={5}
                max={240}
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                data-testid="sc-duration"
              />
            </div>
            <div>
              <Label>Assigned user</Label>
              <Select
                value={form.assigned_to}
                onValueChange={(v) => setForm({ ...form, assigned_to: v })}
              >
                <SelectTrigger data-testid="sc-assigned">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {users.length === 0 && (
                    <SelectItem value="_none" disabled>No team members</SelectItem>
                  )}
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="p-2.5 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-600">
            <div><span className="font-semibold">Lead:</span> {lead?.business_name || "—"}</div>
            {defaultContact && (
              <div><span className="font-semibold">Contact:</span> {defaultContact}</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="sc-contact">Contact name</Label>
              <Input
                id="sc-contact"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                data-testid="sc-contact"
              />
            </div>
            <div>
              <Label htmlFor="sc-phone">Phone number</Label>
              <Input
                id="sc-phone"
                value={form.phone_number}
                onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                placeholder="+1 555 123 4567"
                data-testid="sc-phone"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="sc-notes">Notes</Label>
            <Textarea
              id="sc-notes"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Agenda, prep, context the rep should have on hand…"
              data-testid="sc-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="sc-submit">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Schedule call
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
